import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * Customer-facing order tracking map. Same teardrop-pin vocabulary as the admin's
 * DeliveryMap — station = carbon, driver = propane, customer = info.
 *
 * Accepts coords as props. The caller is responsible for fetching / polling;
 * re-rendering with a new `driver` will smoothly slide the pin.
 */

export type LatLng = [number, number];

interface OrderMapProps {
  destination: LatLng;
  station?: LatLng | null;
  driver?: LatLng | null;
  /** Height in px. Defaults to 240. */
  height?: number;
  className?: string;
}

const PIN_STATION = '#0E0F11'; // carbon-950
const PIN_DRIVER = '#E8501C'; // propane-500
const PIN_CUSTOMER = '#1E6FBF'; // info-500

function teardropPin(color: string, size = 32): L.Icon {
  const width = size;
  const height = (size * 40) / 32;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 40" width="${width}" height="${height}">
      <path d="M16 1 C7.7 1 1 7.7 1 16 C1 26 16 39 16 39 C16 39 31 26 31 16 C31 7.7 24.3 1 16 1 Z"
        fill="${color}" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>
      <circle cx="16" cy="16" r="4.5" fill="#FFFFFF"/>
    </svg>
  `;
  return L.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svg),
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height],
  });
}

const stationIcon = teardropPin(PIN_STATION, 28);
const customerIcon = teardropPin(PIN_CUSTOMER, 32);
const driverIcon = teardropPin(PIN_DRIVER, 32);

function FitBounds({ points }: { points: LatLng[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
    }
  }, [map, points]);
  return null;
}

export function OrderMap({
  destination,
  station = null,
  driver = null,
  height = 240,
  className,
}: OrderMapProps) {
  const points: LatLng[] = [destination];
  if (station) points.push(station);
  if (driver) points.push(driver);

  return (
    <div
      className={className}
      style={{
        height,
        width: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid var(--border)',
      }}
    >
      <MapContainer
        center={destination}
        zoom={15}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={destination} icon={customerIcon} />
        {station && <Marker position={station} icon={stationIcon} />}
        {driver && <Marker position={driver} icon={driverIcon} />}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
